import { Organization } from '@/contexts/auth/types';

const ORGANIZATION_CACHE_KEY = 'cached_organization_settings';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CachedOrganization {
  data: Organization;
  timestamp: number;
}

export const organizationCache = {
  get: (organizationId: string): Organization | null => {
    try {
      const cookieKey = `${ORGANIZATION_CACHE_KEY}_${organizationId}`;
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${cookieKey}=`))
        ?.split('=')[1];

      if (!cookieValue) {
        return null;
      }

      const decoded = decodeURIComponent(cookieValue);
      const cached: CachedOrganization = JSON.parse(decoded);
      
      // Check if cache is still valid
      const now = Date.now();
      if (now - cached.timestamp > CACHE_DURATION) {
        // Cache expired, remove it
        organizationCache.remove(organizationId);
        return null;
      }
      
      return cached.data;
    } catch (error) {
      console.error('Error reading organization cache:', error);
      return null;
    }
  },

  set: (organizationId: string, data: Organization): void => {
    try {
      const cookieKey = `${ORGANIZATION_CACHE_KEY}_${organizationId}`;
      const cached: CachedOrganization = {
        data,
        timestamp: Date.now()
      };
      
      const encoded = encodeURIComponent(JSON.stringify(cached));
      // Set cookie to expire in 24 hours
      const expiryDate = new Date(Date.now() + CACHE_DURATION);
      document.cookie = `${cookieKey}=${encoded}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      
      console.log('🍪 Organization cache set for:', organizationId);
    } catch (error) {
      console.error('Error setting organization cache:', error);
    }
  },

  remove: (organizationId: string): void => {
    try {
      const cookieKey = `${ORGANIZATION_CACHE_KEY}_${organizationId}`;
      document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      console.log('🗑️ Organization cache removed for:', organizationId);
    } catch (error) {
      console.error('Error removing organization cache:', error);
    }
  },

  clear: (): void => {
    try {
      // Remove all organization cache entries
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.startsWith(ORGANIZATION_CACHE_KEY)) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          console.log('🗑️ Organization cache cleared for cookie:', cookieName);
        }
      });
    } catch (error) {
      console.error('Error clearing organization cache:', error);
    }
  },

  invalidate: (organizationId: string): void => {
    console.log('Invalidating organization cache for:', organizationId);
    organizationCache.remove(organizationId);
  },

  clearCurrentOrganization: (): void => {
    try {
      // Get current organization ID from auth context or localStorage
      const currentOrgId = localStorage.getItem('currentOrganizationId');
      if (currentOrgId) {
        organizationCache.remove(currentOrgId);
        console.log('🧹 Cleared current organization cache due to settings change');
      }
    } catch (error) {
      console.error('Error clearing current organization cache:', error);
    }
  },
};